import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Ship, Plane, Truck, Train, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { termsApi, ShipmentType, CreateTermGroupDto } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const SHIPMENT_TYPES: { value: ShipmentType; label: string; icon: React.ReactNode }[] = [
  { value: 'AIR', label: 'Air', icon: <Plane className="w-4 h-4" /> },
  { value: 'OCEAN_FCL', label: 'Ocean FCL', icon: <Ship className="w-4 h-4" /> },
  { value: 'OCEAN_LCL', label: 'Ocean LCL', icon: <Ship className="w-4 h-4" /> },
  { value: 'FTL', label: 'Truck FTL', icon: <Truck className="w-4 h-4" /> },
  { value: 'LTL', label: 'Truck LTL', icon: <Truck className="w-4 h-4" /> },
  { value: 'RAIL', label: 'Rail', icon: <Train className="w-4 h-4" /> },
  { value: 'INTERMODAL', label: 'Intermodal', icon: <Globe className="w-4 h-4" /> },
];

export default function TermsConditionsNew() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<CreateTermGroupDto>({
    name: '',
    description: '',
    shipmentTypes: [],
    regions: [],
  });

  const toggleShipmentType = (type: ShipmentType) => {
    setFormData((prev) => ({
      ...prev,
      shipmentTypes: prev.shipmentTypes?.includes(type)
        ? prev.shipmentTypes.filter((t) => t !== type)
        : [...(prev.shipmentTypes || []), type],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Please provide a group name');
      return;
    }

    try {
      setSaving(true);
      await termsApi.createGroup(formData);
      toast.success('Term group created successfully');
      navigate('/tms/terms');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="New Term Group"
        description="Clauses and version details required to define a contract term set."
        actions={
          <Button variant="outline" onClick={() => navigate('/tms/terms')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Terms &amp; Conditions
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Group Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Group Details</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="name">Group Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Ocean Freight Terms"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description of this terms group..."
                rows={3}
              />
            </div>
          </div>

          {/* Applicable Shipment Types */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Applicable Shipment Types</h3>
            <Separator />

            <div className="flex flex-wrap gap-3">
              {SHIPMENT_TYPES.map((type) => (
                <Badge
                  key={type.value}
                  variant={formData.shipmentTypes?.includes(type.value) ? 'default' : 'outline'}
                  className="cursor-pointer gap-1 px-3 py-1.5 text-sm"
                  onClick={() => toggleShipmentType(type.value)}
                >
                  {type.icon}
                  {type.label}
                </Badge>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/tms/terms')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Term Group'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
